import React from 'react'
import { observer } from 'mobx-react'
import { throttle } from 'lodash'

import withSessionContext, { SessionContext } from 'hocs/withSessionContext'
import InfiniteList from 'components/infiniteList/InfiniteList'
import Mousetrap from 'components/mousetrap/Mousetrap'
import TreeRow from './TreeRow'

import styles from './tree.scss'

interface IProps {
  height: number
  width: number
  recordIds: string[]
}

const TREE_ROW_HEIGHT = 23

class Tree extends React.PureComponent<IProps> {
  context!: SessionContext
  private tree: React.RefObject<any> = React.createRef()
  private scrollIntoView: () => void

  constructor(props: IProps) {
    super(props)

    // Throttle, not debounce because we want to keep scrolling if you hold down a key
    this.scrollIntoView = throttle(this._scrollIntoView, 30, {
      leading: true,
      trailing: true,
    })
  }

  public focus() {
    this.tree.current?.focus()
  }

  public refresh = async () => {
    const { session } = this.context

    await session!.script.run()
  }

  componentDidMount() {
    // Set the selection order so InfiniteList can show the right records
    this.context.session!.selection.tree.setSelectionOrder()
  }

  /* Scrolls the currently active row into view. Don't call this directly, use the throttled version */
  _scrollIntoView = () => {
    const { height } = this.props
    const activeRowIndex = this.getActivePathIndex()
    const list = this.tree.current
    const activeRowTopEdge = activeRowIndex * TREE_ROW_HEIGHT
    const activeRowBottomEdge = activeRowTopEdge + TREE_ROW_HEIGHT

    if (activeRowTopEdge <= list.scrollTop) {
      list.scrollTo({ top: activeRowTopEdge })
    }
    if (activeRowBottomEdge >= list.scrollTop + height) {
      list.scrollTo({ top: activeRowBottomEdge - height })
    }
  }

  getActivePathIndex = () => {
    const { session } = this.context
    return session!.selection.tree.selectionOrder.findIndex(
      o => o === session!.selection.tree.activePath
    )
  }

  handleLoadMore = () => {
    const { session } = this.context

    session!.script.loadMore()
    session!.selection.tree.setSelectionOrder()
  }

  render() {
    const { session } = this.context
    const { height, width, recordIds } = this.props

    const { tree: treeSelection } = session!.selection
    const pathsToRender = treeSelection.selectionOrder

    return (
      <>
        <InfiniteList
          outerElementRef={this.tree}
          className={styles.container}
          height={height - 20 /* Padding outside tree */}
          width={width - 20 /* Padding outside tree */}
          itemCount={pathsToRender.length}
          itemSize={TREE_ROW_HEIGHT}
          itemData={{
            pathsToRender,
            recordIds,
          }}
          itemKey={(idx: number) => pathsToRender[idx]}
          itemRenderer={TreeRow}
          onLoad={this.handleLoadMore}
        />

        {/* Shortcuts */}
        <Mousetrap
          keys="up"
          target={this.tree}
          onMatch={e => {
            treeSelection.move(1, 'up')
            this.scrollIntoView()
          }}
        />
        <Mousetrap
          keys="down"
          target={this.tree}
          onMatch={e => {
            treeSelection.move(1, 'down')
            this.scrollIntoView()
          }}
        />
        <Mousetrap
          keys="left"
          target={this.tree}
          onMatch={e => {
            if (!treeSelection.isExpanded(treeSelection.activePath)) {
              // If already collapsed, jump to parent
              treeSelection.jumpToParent()
            } else {
              treeSelection.collapse()
            }
            this.scrollIntoView()
          }}
        />
        <Mousetrap
          keys="right"
          target={this.tree}
          onMatch={e => {
            treeSelection.expand()
            this.scrollIntoView()
          }}
        />
        <Mousetrap
          keys="space"
          target={this.tree}
          onMatch={e => {
            treeSelection.expand()
            this.scrollIntoView()
          }}
        />
      </>
    )
  }
}

export default observer(withSessionContext(Tree))
